// Admin öğrenci (davetiye) list management: search, add, rename, delete.
// Backs the public booking flow's strict student autocomplete.

import { Pencil, Search, Trash2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { adminApi } from './auth.ts';
import { useDebounce } from './lib/useDebounce.ts';
import { Badge, Button, Card, EmptyState, Input, Modal, PageHeader, useToast } from './ui/index.ts';

type StudentItem = { id: string; name: string; reserved: boolean; referenceCode: string | null };
type ListResp = { items: StudentItem[]; total: number };

function errMsg(err: unknown, fallback: string): string {
  const e = err as { body?: { error?: { message?: string } } };
  return e?.body?.error?.message ?? fallback;
}

export function StudentsList() {
  const toast = useToast();
  const [items, setItems] = useState<StudentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const debouncedQ = useDebounce(query.trim(), 250);

  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const [editing, setEditing] = useState<StudentItem | null>(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleting, setDeleting] = useState<StudentItem | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const qs = debouncedQ ? `?q=${encodeURIComponent(debouncedQ)}` : '';
      const data = await adminApi<ListResp>(`/admin/students${qs}`);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error('Liste yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (name.length < 2) return;
    setAdding(true);
    try {
      await adminApi('/admin/students', { method: 'POST', body: { name } });
      setNewName('');
      toast.success('Öğrenci eklendi.');
      refresh();
    } catch (err) {
      toast.error(errMsg(err, 'Eklenemedi.'));
    } finally {
      setAdding(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const name = editName.trim();
    if (name.length < 2) return;
    setSavingEdit(true);
    try {
      await adminApi(`/admin/students/${editing.id}`, { method: 'PATCH', body: { name } });
      toast.success('Güncellendi.');
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, 'Güncellenemedi.'));
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await adminApi(`/admin/students/${deleting.id}`, { method: 'DELETE' });
      toast.success('Silindi.');
      setDeleting(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err, 'Silinemedi.'));
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Davetiye"
        title="Öğrenciler"
        description={`${total} öğrenci · listede olmayan bir isimle rezervasyon yapılamaz`}
      />

      {/* Add a student */}
      <Card inset>
        <form onSubmit={addStudent} className="flex flex-wrap items-end gap-3">
          <label className="min-w-[220px] flex-1">
            <span className="kicker mb-1.5 block">Yeni öğrenci</span>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ad Soyad"
            />
          </label>
          <Button
            type="submit"
            loading={adding}
            icon={<UserPlus className="h-4 w-4" />}
            disabled={newName.trim().length < 2}
          >
            Ekle
          </Button>
        </form>
      </Card>

      {/* Search + list */}
      <Card>
        <div className="border-b border-border p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-faint" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Öğrenci ara…"
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-text-faint">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Öğrenci yok"
            description={debouncedQ ? 'Aramayla eşleşen öğrenci yok.' : 'Henüz öğrenci eklenmemiş.'}
          />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3 hover:bg-surface">
                <div className="min-w-0 flex-1 truncate text-[15px] text-text">{s.name}</div>
                {s.reserved ? (
                  <Badge tone="gold">Rezerve · {s.referenceCode}</Badge>
                ) : (
                  <Badge tone="muted">Müsait</Badge>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setEditing(s);
                    setEditName(s.name);
                  }}
                  className="rounded p-1.5 text-text-faint transition-colors hover:bg-bg hover:text-text"
                  aria-label={`${s.name} düzenle`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleting(s)}
                  className="rounded p-1.5 text-text-faint transition-colors hover:bg-bg hover:text-danger"
                  aria-label={`${s.name} sil`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Edit modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Öğrenciyi düzenle">
        <div className="space-y-4">
          <label className="block">
            <span className="kicker mb-1.5 block">Ad Soyad</span>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Vazgeç
            </Button>
            <Button onClick={saveEdit} loading={savingEdit} disabled={editName.trim().length < 2}>
              Kaydet
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleting} onClose={() => setDeleting(null)} title="Öğrenciyi sil">
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            <span className="text-text">{deleting?.name}</span> listeden silinecek. Emin misiniz?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Vazgeç
            </Button>
            <Button variant="danger" onClick={confirmDelete} loading={deletingBusy}>
              Sil
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
